import {makeObservable} from 'mobx';

import {api} from '../../api/proto';
import {randomUint8Array} from '../../utils/arrayUtils';
import {AppStore} from '../AppStore';
import BaseTagsStore from './BaseTagsStore';
import ITag from './ITag';
import TagEvent from './TagEvent';
import getCreateUserTagError from './utils/getCreateUserTagError';
import TagType from './TagType';

export default class UserTagsStore extends BaseTagsStore {
  constructor(public app: AppStore) {
    super(app, TagType.Personal);

    makeObservable(this);
  }

  public load = async () => {
    if (this.loading) {
      return;
    }

    this.setLoading(true);
    const {error, res} = await this.app.userStore.userRequest<api.IUserGetTagsResponse>(
      {
        getTags: new api.UserGetTagsRequest({
          userId: this.app.userStore.userId,
          workspaceId: this.app.activeWorkspace.id,
        }),
      },
      'getTags',
    );
    this.setLoading(false);

    if (res?.tags) {
      this.processList(res?.tags);
    }

    return {error, res};
  };

  public create = async (name: string, color: string) => {
    const {error, res} = await this.app.userStore.userRequest<api.IUserCreateTagResponse>(
      {
        createTag: new api.UserCreateTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag: {
            tag: name,
            color,
          },
        }),
      },
      'createTag',
    );

    const errorMessage = getCreateUserTagError(error, res);
    if (!errorMessage) {
      this.processNewTag({
        localID: randomUint8Array(),
        tag: name,
        color,
        workspaceID: this.app.activeWorkspace.id,
        userID: this.app.userStore.userId,
        type: this.type,
      });
      this.emit(TagEvent.CREATE_NEW_TAG, this);
    }

    return {error, res};
  };

  public update = async (tag: ITag) => {
    const {error, res} = await this.app.userStore.userRequest<api.IUserUpdateTagResponse>(
      {
        updateTag: new api.UserUpdateTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag,
        }),
      },
      'updateTag',
    );

    if (!res?.status) {
      this.processEditedTag(tag);
      this.emit(TagEvent.UPDATE_TAG, this);
    }

    return {error, res};
  };

  public delete = async (tag: ITag) => {
    const {error, res} = await this.app.userStore.userRequest<api.IUserDeleteTagResponse>(
      {
        deleteTag: new api.UserDeleteTagRequest({
          workspaceId: this.app.activeWorkspace.id,
          tag,
        }),
      },
      'deleteTag',
    );

    if (!res?.status) {
      this.processDeletedTag(tag);
      this.emit(TagEvent.DELETE_TAG, this);
    }

    return {error, res};
  };

  public reorderTags = async (tagIds: Uint8Array[]) => {
    const {error, res} = await this.app.userStore.userRequest<api.IUserReorderTagsResponse>(
      {
        reorderTags: new api.UserReorderTagsRequest({
          workspaceId: this.app.activeWorkspace.id,
          userId: this.app.userStore.userId,
          ids: tagIds,
        }),
      },
      'reorderTags',
    );

    if (!res?.status) {
      this.processReorderTags(tagIds);
    }

    return {error, res};
  };
}